import { DOCUMENT } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { Store } from '@ngxs/store';
import { VxIconButtonComponent } from '@ui/icon-button/icon-button.component';
import { SubscriptionStatus } from 'src/app/models/subscription-plan';
import { CheckRoleDirective } from 'src/app/services/permission/permission.directive';
import { SubscriptionState } from 'src/app/store/subscription/subscription.state';

/** Per-browser: the day the banner was last closed (YYYY-MM-DD). */
const DISMISSED_ON_KEY = 'incidite.trialBanner.dismissedOn';

/** In the trial's last days the banner can't be closed for the day. */
const ALWAYS_SHOWN_FROM_DAYS = 3;

/**
 * "N days until your free trial expires · Upgrade now" above the content
 * panel, for subscription managers while the organization is trialing.
 * Closing it hides it for the rest of the day, except in the last
 * `ALWAYS_SHOWN_FROM_DAYS` days.
 */
@Component({
  selector: 'app-trial-banner',
  templateUrl: './trial-banner.component.html',
  imports: [
    CheckRoleDirective,
    RouterLink,
    TranslocoModule,
    VxIconButtonComponent,
  ],
  host: { class: 'contents' },
})
export class TrialBannerComponent {
  private readonly store = inject(Store);
  private readonly document = inject(DOCUMENT);

  private readonly subscription = this.store.selectSignal(
    SubscriptionState.subscription
  );

  private readonly dismissedToday = signal(
    this.readDismissedOn() === this.today()
  );

  protected readonly daysLeft = computed(() => {
    const subscription = this.subscription().data;
    return subscription?.status === SubscriptionStatus.TRIALING
      ? subscription.daysRemainingInTrial
      : null;
  });

  protected readonly closable = computed(
    () => (this.daysLeft() ?? 0) > ALWAYS_SHOWN_FROM_DAYS
  );

  protected readonly visible = computed(
    () =>
      this.daysLeft() !== null && !(this.closable() && this.dismissedToday())
  );

  protected dismiss(): void {
    this.dismissedToday.set(true);
    try {
      this.document.defaultView?.localStorage.setItem(
        DISMISSED_ON_KEY,
        this.today()
      );
    } catch {
      // storage blocked: hidden for this visit only
    }
  }

  private readDismissedOn(): string | null {
    try {
      return (
        this.document.defaultView?.localStorage.getItem(DISMISSED_ON_KEY) ??
        null
      );
    } catch {
      return null;
    }
  }

  private today(): string {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  }
}
